import { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Bell, Shield, User, Key, Download, Trash2, AlertCircle, Eye, EyeOff, ChevronRight, ChevronLeft, XCircle, Lock, Mail, Info, HelpCircle, FileText, LogOut, X, AlertTriangle, BellOff, Crown, Check, CreditCard, MapPin, Phone } from 'lucide-react';
import { Loader } from './ui/Loader';
import { projectId, API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { fetchWithAuth } from '../utils/api';
import { devLog, devError } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { PageShell } from './layout/PageShell';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';

interface SettingsPageProps {
  onNavigate: (view: string) => void;
  currentSettings: {
    hideOnlineStatus?: boolean;
  };
  onSignOut: () => void;
  onProfileUpdate?: () => void;
  /** Opens checkout in update-payment mode; after success user returns to settings */
  onOpenUpdatePayment?: () => void;
}

export function SettingsPage({ 
  onNavigate, 
  currentSettings,
  onSignOut,
  onProfileUpdate,
  onOpenUpdatePayment
}: SettingsPageProps) {
  const { session, userProfile } = useAuth();
  const accessToken = session?.access_token ?? '';
  const SETTINGS_SECTION_KEY = 'talentfunn_settings_selected_section';

  // Initialize from sessionStorage so returning from FAQ/Privacy/Terms keeps "Hjelp og info" selected (desktop)
  const [selectedSection, setSelectedSection] = useState<string | null>(() =>
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SETTINGS_SECTION_KEY) || 'privacy' : 'privacy'
  );

  // Scroll to top only for fresh entries (not when returning with preserved scroll).
  useEffect(() => {
    const hasSavedScroll = sessionStorage.getItem('talentfunn_scroll_settings') !== null;
    if (!hasSavedScroll) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    if (!selectedSection) return;
    sessionStorage.setItem(SETTINGS_SECTION_KEY, selectedSection);
  }, [selectedSection]);

  const [hideOnlineStatus, setHideOnlineStatus] = useState(currentSettings.hideOnlineStatus || false);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [pendingHideOnlineStatus, setPendingHideOnlineStatus] = useState<boolean | null>(null);
  const privacyPendingTimerRef = useRef<number | null>(null);

  const clearPrivacyPending = () => {
    if (privacyPendingTimerRef.current !== null) {
      window.clearTimeout(privacyPendingTimerRef.current);
      privacyPendingTimerRef.current = null;
    }
    setPendingHideOnlineStatus(null);
  };

  const armPrivacyPendingFallback = () => {
    if (privacyPendingTimerRef.current !== null) {
      window.clearTimeout(privacyPendingTimerRef.current);
    }
    // Safety net: avoid indefinite "pending" if parent profile refresh is delayed/missed.
    privacyPendingTimerRef.current = window.setTimeout(() => {
      setPendingHideOnlineStatus(null);
      privacyPendingTimerRef.current = null;
    }, 5000);
  };

  // ✅ CRITICAL: Sync hideOnlineStatus state with currentSettings when it changes.
  // During an optimistic save, ignore stale parent values until they catch up.
  useEffect(() => {
    if (privacyLoading) return;
    const serverValue = currentSettings.hideOnlineStatus || false;
    if (pendingHideOnlineStatus !== null) {
      if (serverValue === pendingHideOnlineStatus) {
        clearPrivacyPending();
        setHideOnlineStatus(serverValue);
      }
      return;
    }
    setHideOnlineStatus(serverValue);
  }, [currentSettings.hideOnlineStatus, privacyLoading, pendingHideOnlineStatus]);

  const [marketingConsent, setMarketingConsent] = useState(userProfile?.marketingEmailsConsent || false);
  const [marketingConsentLoading, setMarketingConsentLoading] = useState(false);
  const [pendingMarketingConsent, setPendingMarketingConsent] = useState<boolean | null>(null);
  const marketingPendingTimerRef = useRef<number | null>(null);
  const clearMarketingPending = () => {
    if (marketingPendingTimerRef.current !== null) {
      window.clearTimeout(marketingPendingTimerRef.current);
      marketingPendingTimerRef.current = null;
    }
    setPendingMarketingConsent(null);
  };
  const armMarketingPendingFallback = () => {
    if (marketingPendingTimerRef.current !== null) {
      window.clearTimeout(marketingPendingTimerRef.current);
    }
    // Safety net: avoid indefinite "pending" if parent profile refresh is delayed/missed.
    marketingPendingTimerRef.current = window.setTimeout(() => {
      setPendingMarketingConsent(null);
      marketingPendingTimerRef.current = null;
    }, 5000);
  };
  useEffect(() => {
    if (marketingConsentLoading) return;
    const serverValue = userProfile?.marketingEmailsConsent || false;
    if (pendingMarketingConsent !== null) {
      if (serverValue === pendingMarketingConsent) {
        clearMarketingPending();
        setMarketingConsent(serverValue);
      }
      return;
    }
    setMarketingConsent(serverValue);
  }, [userProfile?.marketingEmailsConsent, marketingConsentLoading, pendingMarketingConsent]);

  useEffect(() => {
    return () => {
      if (privacyPendingTimerRef.current !== null) {
        window.clearTimeout(privacyPendingTimerRef.current);
      }
      if (marketingPendingTimerRef.current !== null) {
        window.clearTimeout(marketingPendingTimerRef.current);
      }
    };
  }, []);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPreferences, setPushPreferences] = useState({
    systemMessages: true,
    newMessages: true
  });
  const [saving, setSaving] = useState(false);
  const [showPushDialog, setShowPushDialog] = useState(false);

  // Account settings states
  const [newEmail, setNewEmail] = useState('');
  const [showDiscardEmailConfirm, setShowDiscardEmailConfirm] = useState(false);
  const [pendingEmailAction, setPendingEmailAction] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    isDirty: isEmailDirty,
    markDirty: markEmailDirty,
    resetDirty: resetEmailDirty,
    setDirty: setEmailDirty,
  } = useDirtyFormGuard(false);

  const { clearDraft: clearEmailDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:settings-email:${session?.user?.id ?? 'anon'}`,
    enabled: true,
    value: { newEmail },
    onRestore: (payload: { newEmail?: string }) => {
      setNewEmail(payload?.newEmail || '');
      if ((payload?.newEmail || '').trim()) setEmailDirty(true);
    },
  });

  useBeforeUnloadDirty(isEmailDirty && !!newEmail.trim() && !loading);

  const handleEmailInputChange = (value: string) => {
    setNewEmail(value);
    if (value.trim()) {
      markEmailDirty();
    } else {
      clearEmailDraft();
      resetEmailDirty();
    }
  };

  const navigateWithEmailGuard = (view: string) => {
    if (isEmailDirty && newEmail.trim()) {
      setPendingEmailAction(`navigate:${view}`);
      setShowDiscardEmailConfirm(true);
      return;
    }
    clearEmailDraft();
    resetEmailDirty();
    onNavigate(view);
  };

  const signOutWithEmailGuard = () => {
    if (isEmailDirty && newEmail.trim()) {
      setPendingEmailAction('signout');
      setShowDiscardEmailConfirm(true);
      return;
    }
    clearEmailDraft();
    resetEmailDirty();
    onSignOut();
  };

  const confirmDiscardEmailChanges = () => {
    const action = pendingEmailAction;
    setShowDiscardEmailConfirm(false);
    setPendingEmailAction(null);
    setNewEmail('');
    clearEmailDraft();
    resetEmailDirty();
    if (!action) return;
    if (action === 'signout') {
      onSignOut();
      return;
    }
    if (action.startsWith('navigate:')) {
      onNavigate(action.replace('navigate:', ''));
    }
  };

  // Subscription status for desktop "Abonnement" section (plan name + next payment)
  const [subscriptionData, setSubscriptionData] = useState<{
    status?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
    cancel_at_period_end?: boolean;
    daysLeftInGrace?: number;
  } | null>(null);
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(false);

  // Check if push notifications are enabled and fetch status; refetch when accessToken changes (e.g. after re-login)
  useEffect(() => {
    if (!accessToken) return;
    if ('Notification' in window) {
      setPushNotifications(Notification.permission === 'granted');
    }
    fetchPushStatus();
    fetchPushPreferences();
  }, [accessToken]);

  // Keep push settings device-local: refresh when app/tab becomes active.
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchPushStatus();
        fetchPushPreferences();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [accessToken]);

  // Fetch subscription status when user opens Abonnement section (desktop) so we can show active plan + next payment
  useEffect(() => {
    if (selectedSection !== 'subscription' || !accessToken) return;
    let cancelled = false;
    setSubscriptionStatusLoading(true);
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/nets/subscription-status`);
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          devLog('subscription-status fetch failed in SettingsPage', {
            status: res.status,
            requestId: data?.requestId ?? res.headers.get('X-Request-Id') ?? undefined,
            stage: data?.stage,
            error: data?.error,
          });
        }
        if (!cancelled && data?.subscription) setSubscriptionData(data.subscription);
      } catch {
        if (!cancelled) setSubscriptionData(null);
      } finally {
        if (!cancelled) setSubscriptionStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSection, accessToken]);

  const fetchPushStatus = async () => {
    try {
      // 1. Check backend subscription status
      const response = await fetch(
        `${API_BASE}/push-subscription/status`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const dbSubscribed = data.subscribed || false;
        
        // 2. Check browser subscription status
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          const browserSubscription = await registration.pushManager.getSubscription();
          
          // 3. Detect mismatch: DB says subscribed, but browser has no subscription
          if (dbSubscribed && !browserSubscription) {
            // ✅ CRITICAL FIX: Only auto-resubscribe if permission is already granted
            // On iOS, calling pushManager.subscribe() triggers permission prompt if permission is 'default'
            if (Notification.permission !== 'granted') {
              devLog('[PUSH SYNC] Permission not granted - skipping auto-resubscribe. User must enable manually.');
              setPushSubscribed(false);
              return;
            }
            
            devLog('[PUSH SYNC] Mismatch detected - DB has subscription but browser does not. Auto-resubscribing...');
            
            // ✅ AUTO-RESUBSCRIBE: Instead of deleting, recreate the subscription silently
            try {
              // Fetch VAPID public key
              const keyResponse = await fetch(
                `${API_BASE}/vapid-public-key`
              );
              
              if (keyResponse.ok) {
                const { publicKey } = await keyResponse.json();
                
                // Subscribe to push with VAPID public key
                const newSubscription = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: urlBase64ToUint8Array(publicKey),
                });

                devLog('[PUSH SYNC] Browser subscription recreated:', newSubscription.endpoint);

                // Send new subscription to backend (will update existing record)
                const response = await fetch(
                  `${API_BASE}/push-subscription`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(newSubscription.toJSON()),
                  }
                );

                if (response.ok) {
                  devLog('[PUSH SYNC] Subscription updated successfully! User will continue receiving notifications.');
                  setPushSubscribed(true);
                } else {
                  devError('[PUSH SYNC] Failed to save new subscription to backend');
                  setPushSubscribed(false);
                }
              }
            } catch (autoSyncError) {
              devError('[PUSH SYNC] Auto-resubscribe failed:', autoSyncError);
              // Fallback: Mark as unsubscribed if auto-resubscribe fails
              setPushSubscribed(false);
            }
          } else {
            setPushSubscribed(dbSubscribed);
          }
        } else {
          setPushSubscribed(dbSubscribed);
        }
      }
    } catch (error) {
      devLog('Error fetching push status:', error);
    }
  };

  const fetchPushPreferences = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/push-subscription/preferences`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPushPreferences(data.preferences || { systemMessages: true, newMessages: true });
      }
    } catch (error) {
      devLog('Error fetching push preferences:', error);
    }
  };

  const updatePushPreferences = async (newPreferences: typeof pushPreferences) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE}/push-subscription/preferences`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ preferences: newPreferences }),
        }
      );

      if (response.ok) {
        setPushPreferences(newPreferences);
      } else {
        toast.error('Kunne ikke oppdatere innstillinger');
      }
    } catch (error) {
      devLog('Error updating preferences:', error);
      toast.error('Noe gikk galt');
    } finally {
      setSaving(false);
    }
  };

  const handlePushToggle = async () => {
    // ✅ Step 1: Check if Notification API exists
    if (!('Notification' in window)) {
      toast.error('Push-varsler støttes ikke i denne nettleseren');
      return;
    }

    // ✅ Step 2: Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // ✅ Step 3: Check if running in standalone mode (PWA installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone === true;

    // ✅ Step 4: iOS-specific checks
    if (isIOS) {
      if (!isStandalone) {
        toast.error(
          'Push-varsler på iOS',
          'På iOS må appen installeres på hjemskjermen først. Trykk på Del-knappen i Safari og velg "Legg til på Hjem-skjerm".'
        );
        return;
      }

      // Check iOS version (16.4+ required for push)
      const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
      if (match) {
        const majorVersion = parseInt(match[1], 10);
        const minorVersion = parseInt(match[2], 10);
        
        if (majorVersion < 16 || (majorVersion === 16 && minorVersion < 4)) {
          toast.error(
            'Push-varsler krever iOS 16.4+',
            `Din versjon: iOS ${majorVersion}.${minorVersion}. Oppdater iOS for å aktivere push-varsler.`
          );
          return;
        }
      }
    }

    // ✅ Step 5: Check if PushManager is available
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push-varsler støttes ikke', 'Nettleseren din støtter ikke push-varsler.');
      return;
    }

    // If browser permission is denied, show instruction
    if (Notification.permission === 'denied') {
      // Detect Android for platform-specific instructions
      const isAndroid = /Android/i.test(navigator.userAgent);
      
      if (isAndroid) {
        toast.warning(
          'Push-varsler er blokkert', 
          'Gå til Chrome-innstillinger → Nettstedsinnstillinger → Varsler, finn talentfunn.no og aktiver varsler. Start appen på nytt etterpå.'
        );
      } else {
        toast.warning(
          'Push-varsler er blokkert', 
          'Gå til nettleserens innstillinger for å aktivere varsler for Talentfunn'
        );
      }
      return;
    }

    // If permission is not granted yet, show custom dialog first
    if (Notification.permission === 'default') {
      setShowPushDialog(true);
      return;
    }

    // If permission is granted, toggle subscription in DB
    if (Notification.permission === 'granted') {
      if (pushSubscribed) {
        // Unsubscribe
        await unsubscribeFromPush();
      } else {
        // Subscribe
        await subscribeToPush();
      }
    }
  };

  const handleConfirmPushDialog = async () => {
    setShowPushDialog(false);
    
    try {
      // Request browser permission
      const permission = await Notification.requestPermission();
      setPushNotifications(permission === 'granted');
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        await subscribeToPush();
      }
    } catch (error) {
      devError('Error requesting notification permission:', error);
      toast.error('Kunne ikke aktivere push-varsler');
    }
  };

  const subscribeToPush = async () => {
    setSaving(true);
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        
        devLog('[PUSH] Service Worker ready, subscribing...');
        
        // ✅ Unsubscribe old subscription first (fixes VAPID key mismatch)
        const oldSubscription = await registration.pushManager.getSubscription();
        if (oldSubscription) {
          devLog('[PUSH] Unsubscribing old subscription first...');
          await oldSubscription.unsubscribe();
        }
        
        // ✅ Fetch VAPID public key from backend
        devLog('[PUSH] Fetching VAPID public key from backend...');
        const keyResponse = await fetch(
          `${API_BASE}/vapid-public-key`
        );
        
        if (!keyResponse.ok) {
          throw new Error('Failed to fetch VAPID public key');
        }
        
        const { publicKey } = await keyResponse.json();
        devLog('[PUSH] Received VAPID public key from backend');
        
        // Subscribe to push with VAPID public key
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        devLog('[PUSH] Browser subscription created:', subscription.endpoint);

        // Send subscription to backend
        const response = await fetch(
          `${API_BASE}/push-subscription`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(subscription.toJSON()), // ✅ FIXED: Use .toJSON() to serialize properly
          }
        );

        devLog('[PUSH] Backend response status:', response.status);

        if (response.ok) {
          devLog('[PUSH] Subscription saved successfully!');
          setPushSubscribed(true);
          await updatePushPreferences({ systemMessages: true, newMessages: true });
          toast.success('Push-varsler aktivert!');
        } else {
          const errorData = await response.json();
          devError('[PUSH] Backend error:', errorData);
          throw new Error(errorData.error || 'Failed to save subscription');
        }
      }
    } catch (error) {
      devError('[PUSH] Error subscribing to push:', error);
      toast.error(`Kunne ikke aktivere push-varsler: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setSaving(true);
    try {
      // Get current subscription endpoint
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      devLog('[PUSH] Unsubscribing from push notifications...');
      
      if (!subscription) {
        devLog('[PUSH] No subscription found, marking as unsubscribed');
        setPushSubscribed(false);
        toast.success('Push-varsler deaktivert');
        return;
      }

      // Remove subscription from backend
      const response = await fetch(
        `${API_BASE}/push-subscription`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        }
      );

      devLog('[PUSH] Backend unsubscribe response:', response.status);

      if (response.ok) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        devLog('[PUSH] Successfully unsubscribed from push notifications');
        setPushSubscribed(false);
        toast.success('Push-varsler deaktivert');
      } else {
        const errorData = await response.json();
        devError('[PUSH] Backend unsubscribe error:', errorData);
        throw new Error(errorData.error || 'Failed to remove subscription');
      }
    } catch (error) {
      devError('[PUSH] Error unsubscribing from push:', error);
      toast.error('Kunne ikke deaktivere push-varsler');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handlePrivacyToggle = async (newValue: boolean) => {
    // Set loading state BEFORE changing value
    setPrivacyLoading(true);
    const previousValue = hideOnlineStatus;
    setPendingHideOnlineStatus(newValue);
    armPrivacyPendingFallback();
    const loadingToastId = toast.loading(
      newValue ? 'Skjuler aktiv nå-status...' : 'Viser aktiv nå-status...'
    );
    
    // Optimistically update UI
    setHideOnlineStatus(newValue);

    try {
      const response = await fetch(
        `${API_BASE}/profile/privacy`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            hideOnlineStatus: newValue,
          }),
        }
      );

      if (response.ok) {
        // ✅ CRITICAL: Refresh user profile in App.tsx to sync state
        if (onProfileUpdate) {
          onProfileUpdate();
        }
        toast.dismiss(loadingToastId);
        toast.success('Innstilling lagret');
      } else {
        toast.dismiss(loadingToastId);
        toast.error('Kunne ikke lagre innstilling');
        clearPrivacyPending();
        // Revert on error
        setHideOnlineStatus(previousValue);
      }
    } catch (error) {
      devLog('Error saving setting:', error);
      toast.dismiss(loadingToastId);
      toast.error('Kunne ikke lagre innstilling');
      clearPrivacyPending();
      // Revert on error
      setHideOnlineStatus(previousValue);
    } finally {
      toast.dismiss(loadingToastId);
      setPrivacyLoading(false);
    }
  };

  const handleMarketingConsentToggle = async (newValue: boolean) => {
    setMarketingConsentLoading(true);
    const previousValue = marketingConsent;
    setPendingMarketingConsent(newValue);
    armMarketingPendingFallback();
    const loadingToastId = toast.loading(
      newValue ? 'Aktiverer markedsføring på e-post...' : 'Deaktiverer markedsføring på e-post...'
    );
    
    // Optimistically update UI
    setMarketingConsent(newValue);

    try {
      const response = await fetch(
        `${API_BASE}/update-marketing-consent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            consent: newValue,
          }),
        }
      );

      if (response.ok) {
        // Refresh user profile to sync state
        if (onProfileUpdate) {
          onProfileUpdate();
        }
        toast.dismiss(loadingToastId);
        toast.success(newValue ? 'Du vil motta markedsføring på e-post' : 'Du vil ikke motta markedsføring');
      } else {
        toast.dismiss(loadingToastId);
        toast.error('Kunne ikke lagre innstilling');
        clearMarketingPending();
        // Revert on error
        setMarketingConsent(previousValue);
      }
    } catch (error) {
      devLog('Error saving marketing consent:', error);
      toast.dismiss(loadingToastId);
      toast.error('Kunne ikke lagre innstilling');
      clearMarketingPending();
      // Revert on error
      setMarketingConsent(previousValue);
    } finally {
      toast.dismiss(loadingToastId);
      setMarketingConsentLoading(false);
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(
        `${API_BASE}/account/update-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ newEmail }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Bekreftelses-e-post sendt! Sjekk din nye e-postadresse og klikk på lenken for å fullføre endringen.');
        setNewEmail('');
        clearEmailDraft();
        resetEmailDirty();
      } else {
        // Translate Supabase error messages to Norwegian
        let errorMessage = data.error || 'Kunne ikke oppdatere e-post';
        
        if (errorMessage.includes('Email rate limit exceeded')) {
          errorMessage = 'For mange e-poster sendt. Vennligst vent litt før du prøver igjen';
        } else if (errorMessage.includes('Invalid email')) {
          errorMessage = 'Ugyldig e-postadresse';
        } else if (errorMessage.includes('Email already registered') || errorMessage.includes('already in use')) {
          errorMessage = 'Denne e-postadressen er allerede i bruk';
        } else if (errorMessage.includes('same as the old email')) {
          errorMessage = 'Den nye e-postadressen kan ikke være den samme som den gamle';
        } else if (errorMessage.includes('Unable to validate')) {
          errorMessage = 'Kunne ikke validere e-postadresse';
        } else if (errorMessage.includes('For security purposes')) {
          errorMessage = 'Av sikkerhetshensyn må du vente litt før du prøver igjen';
        }
        
        setErrorMessage(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMsg = 'Noe gikk galt. Prøv igjen senere.';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      devError('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    setErrorMessage('');

    try {
      const response = await fetchWithAuth(`${API_BASE}/export-my-data`, { method: 'GET' });

      const data = await response.json();

      if (response.ok) {
        const escapeHtml = (value: unknown): string =>
          String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

        const formatDate = (value: unknown): string => {
          if (!value || typeof value !== 'string') return '-';
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? value : date.toLocaleString('nb-NO');
        };

        const sectionRows = Array.isArray(data?.readable?.sections)
          ? data.readable.sections
              .map((section: any) => {
                const title = escapeHtml(section?.title ?? 'Kategori');
                const count = Number(section?.count ?? 0);
                return `<tr><td>${title}</td><td style="text-align:right">${count}</td></tr>`;
              })
              .join('')
          : '';

        const ownerName = escapeHtml(data?.readable?.owner?.name || data?.profile?.name || '-');
        const ownerEmail = escapeHtml(data?.readable?.owner?.email || data?.account?.email || '-');
        const ownerType = escapeHtml(data?.readable?.owner?.userType || data?.metadata?.userType || '-');
        const ownerSub = escapeHtml(data?.readable?.owner?.subscription || data?.profile?.subscription || '-');
        const generatedAt = formatDate(data?.exportDate || data?.readable?.generatedAt);

        const htmlReport = `<!doctype html>
<html lang="nb">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Talentfunn dataeksport</title>
    <style>
      body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #111827; line-height: 1.45; }
      h1 { margin: 0 0 8px; color: #4c1d95; }
      h2 { margin-top: 26px; color: #5b21b6; }
      .meta { background: #f5f3ff; border: 1px solid #ddd6fe; padding: 14px; border-radius: 10px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th, td { border-bottom: 1px solid #e5e7eb; padding: 10px 8px; text-align: left; }
      th { background: #faf5ff; }
      .muted { color: #6b7280; font-size: 14px; }
      .box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
    </style>
  </head>
  <body>
    <h1>Talentfunn - Mine data</h1>
    <p class="muted">Generert: ${escapeHtml(generatedAt)}</p>
    <div class="meta">
      <p><strong>Navn:</strong> ${ownerName}</p>
      <p><strong>E-post:</strong> ${ownerEmail}</p>
      <p><strong>Brukertype:</strong> ${ownerType}</p>
      <p><strong>Abonnement:</strong> ${ownerSub}</p>
    </div>
    <h2>Oversikt</h2>
    <table>
      <thead>
        <tr><th>Datakategori</th><th style="text-align:right">Antall</th></tr>
      </thead>
      <tbody>${sectionRows || '<tr><td colspan="2">Ingen kategorier tilgjengelig</td></tr>'}</tbody>
    </table>
    <h2>Viktig informasjon</h2>
    <div class="box">
      <p>Dette er en lesbar rapport av dataeksporten din. For maskinlesbar portabilitet (GDPR Art. 20), bruk JSON-filen som ble lastet ned samtidig.</p>
      <p class="muted">Tekniske sikkerhetsfelt og interne nøkler er filtrert bort i denne eksporten.</p>
    </div>
  </body>
</html>`;

        const fileDate = new Date().toISOString().split('T')[0];
        const reportBlob = new Blob([htmlReport], { type: 'text/html;charset=utf-8' });
        const reportUrl = URL.createObjectURL(reportBlob);
        const reportLink = document.createElement('a');
        reportLink.href = reportUrl;
        reportLink.download = `talentfunn-mine-data-${fileDate}.html`;
        document.body.appendChild(reportLink);
        reportLink.click();
        document.body.removeChild(reportLink);
        URL.revokeObjectURL(reportUrl);

        // Download machine-readable export as well (GDPR portability).
        const dataStr = JSON.stringify(data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        // Create download link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `talentfunn-mine-data-${fileDate}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('Dine data er lastet ned');
      } else {
        setErrorMessage(data.error || 'Kunne ikke eksportere data');
      }
    } catch (err) {
      setErrorMessage('Noe gikk galt. Prøv igjen senere.');
      devError('Error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(
        `${API_BASE}/profile`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Account deleted successfully - Auth user is already deleted by backend
        // Clear localStorage to prevent stale session from triggering login attempts
        localStorage.clear();
        
        toast.success('Din konto er permanent slettet.');
        setShowDeleteConfirm(false);
        
        setTimeout(() => {
          window.location.href = '/'; // ✅ Redirect - localStorage cleared, no session found
        }, 1500);
      } else {
        setErrorMessage(data.error || 'Kunne ikke slette konto');
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      setErrorMessage('Noe gikk galt. Prøv igjen senere.');
      devError('Error:', err);
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
      {/* Content - scroll handled by main / App wrapper */}
      <PageShell className="pt-page-safe-sm settings-spacing xl:max-w-6xl xl:mx-auto xl:px-0 xl:pt-5 xl:pb-24">
          
          {/* Content container - fullscreen on mobile, sidebar+panel on desktop */}
          <div className="bg-white pt-0 xl:pt-0 xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:overflow-hidden">

            {/* ===== MOBILE: stacked layout ===== */}
            <div className="xl:hidden">

            {/* Error Message */}
            {errorMessage && (
              <div className="mx-4 mt-4 xl:mx-6 xl:mt-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* Privacy Settings */}
            <div className="border-b border-gray-200">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Personvern</h3>
              </div>
              
              {/* Hide Online Status */}
              <div className="px-5 py-3 border-b border-gray-100">
                <button
                  onClick={() => handlePrivacyToggle(!hideOnlineStatus)}
                  disabled={privacyLoading}
                  className="w-full flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center gap-3">
                    {hideOnlineStatus ? (
                      <EyeOff className="w-5 h-5 text-gray-600" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-600" />
                    )}
                    <div className="text-left">
                      <p className="text-base text-gray-900">Skjul "Aktiv nå" status</p>
                      <p className="text-sm text-gray-500">Andre ser ikke om du er online</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {privacyLoading && <Loader size="sm" className="text-purple-600" />}
                    <div
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        hideOnlineStatus ? 'bg-purple-600' : 'bg-gray-300'
                      } ${privacyLoading ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          hideOnlineStatus ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </div>
                  </div>
                </button>
              </div>

              {/* Marketing Consent */}
              <div className="px-5 py-3">
                <button
                  onClick={() => handleMarketingConsentToggle(!marketingConsent)}
                  disabled={marketingConsentLoading}
                  className="w-full flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <div className="text-left">
                      <p className="text-base text-gray-900">Motta markedsføring på e-post</p>
                      <p className="text-sm text-gray-500">Nyhetsbrev, tips og kampanjer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {marketingConsentLoading && <Loader size="sm" className="text-purple-600" />}
                    <div
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        marketingConsent ? 'bg-purple-600' : 'bg-gray-300'
                      } ${marketingConsentLoading ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          marketingConsent ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </div>
                  </div>
                </button>
                
                {/* Info text below toggle - REMOVED: User requested to remove this as it's already in Terms */}
              </div>
            </div>

            {/* Notifications */}
            <div className="border-b border-gray-200">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Varsler</h3>
              </div>
              
              {/* Push Master Toggle */}
              <div className="px-5 py-3">
                <button
                  onClick={handlePushToggle}
                  disabled={saving}
                  className="w-full flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center gap-3">
                    {pushSubscribed ? (
                      <Bell className="w-5 h-5 text-gray-600" />
                    ) : (
                      <BellOff className="w-5 h-5 text-gray-600" />
                    )}
                    <div className="text-left">
                      <p className="text-base text-gray-900">Push-varsler</p>
                      <p className="text-sm text-gray-500">Motta melding og systemvarsler</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {saving && <Loader size="sm" className="text-purple-600" />}
                    <div
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        pushSubscribed ? 'bg-purple-600' : 'bg-gray-300'
                      } ${saving ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          pushSubscribed ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Account */}
            <div className="border-b border-gray-200">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Konto</h3>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <p className="text-sm font-medium text-gray-700">Endre e-postadresse</p>
                  </div>
                  <form onSubmit={handleUpdateEmail} className="space-y-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => handleEmailInputChange(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                      placeholder="ny@epost.no"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm font-medium active:scale-95"
                    >
                      {loading ? 'Oppdaterer...' : 'Oppdater e-post'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Subscription */}
            <div className="border-b border-gray-200">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Abonnement</h3>
              </div>
              <div className="px-5 py-3">
                <button
                  onClick={() => navigateWithEmailGuard('subscription')}
                  className="w-full flex items-center justify-between py-2 active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center gap-3">
                    <Crown className="w-5 h-5 text-gray-600" />
                    <div className="text-left">
                      <p className="text-base text-gray-900">Administrer abonnement</p>
                      <p className="text-sm text-gray-500">Se planer og fakturering</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Help & Info */}
            <div className="border-b border-gray-200">
              <div className="px-5 pt-5 pb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hjelp & Info</h3>
              </div>
              <div className="px-5 py-1">
                {/* FAQ */}
                <button
                  onClick={() => navigateWithEmailGuard('faq')}
                  className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform border-b border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-gray-600" />
                    <span className="text-base text-gray-900">Ofte stilte spørsmål (FAQ)</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* Privacy Policy */}
                <button
                  onClick={() => navigateWithEmailGuard('privacy')}
                  className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform border-b border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-gray-600" />
                    <span className="text-base text-gray-900">Personvernerklæring</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* Terms */}
                <button
                  onClick={() => navigateWithEmailGuard('terms')}
                  className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform border-b border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="text-base text-gray-900">Vilkår for bruk</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* About */}
                <button
                  onClick={() => navigateWithEmailGuard('about')}
                  className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform border-b border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <Info className="w-5 h-5 text-gray-600" />
                    <span className="text-base text-gray-900">Om Talentfunn</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {/* Kontakt oss – Vipps compliance: godt synlig firma- og kontaktinformasjon */}
                <div className="py-4 px-1">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Kontakt oss</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="font-medium text-gray-900">Innovara AS</p>
                    <p>Org.nr. 933 184 048</p>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Blåknappveien 9, 4823 Nedenes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <a href="tel:94146523" className="text-purple-600 hover:underline">94 14 65 23</a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <a href="mailto:kontakt@talentfunn.no" className="text-purple-600 hover:underline">kontakt@talentfunn.no</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* GDPR Data Management */}
            {!userProfile?.isAdmin && (
              <div className="border-b border-gray-200">
                <div className="px-5 pt-5 pb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mine Data</h3>
                </div>
                <div className="px-5 py-1">
                  {/* Export Data */}
                  <button
                    onClick={handleExportData}
                    disabled={exportLoading}
                    className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform border-b border-gray-100 disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <p className="text-base text-gray-900">Last ned mine data</p>
                        <p className="text-sm text-gray-500">Eksporter data i JSON-format (GDPR)</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>

                  {/* Delete Account */}
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full flex items-center justify-between py-3 active:scale-[0.98] transition-transform"
                  >
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-red-600" />
                      <div className="text-left">
                        <p className="text-base text-red-600 font-medium">Slett kontoen min</p>
                        <p className="text-sm text-gray-500">Permanent sletting av all data</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
            )}

            {/* Sign Out - Standalone red button */}
            <div className="px-5 py-6">
              <button
                onClick={signOutWithEmailGuard}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-red-50 hover:bg-red-100 border-2 border-red-200 rounded-xl transition-all active:scale-[0.98]"
              >
                <LogOut className="w-5 h-5 text-red-600" />
                <span className="text-base font-semibold text-red-600">Logg ut</span>
              </button>
            </div>

            </div>{/* end mobile stacked layout */}

            {/* ===== DESKTOP: 2-column sidebar + content panel (fixed width = same as subscription) ===== */}
            <div className="hidden xl:grid xl:grid-cols-[240px_1fr] xl:w-[72rem] xl:min-w-[72rem]" style={{ minHeight: '600px' }}>
              {/* Sidebar */}
              <aside className="border-r border-gray-200 bg-gray-50/50 flex flex-col">
                <div className="px-6 pt-6 pb-4">
                  <h1 className="text-xl font-bold text-gray-900">Innstillinger</h1>
                </div>
                <nav className="flex-1 px-3 space-y-0.5">
                  {[
                    { id: 'privacy', label: 'Personvern', Icon: Shield },
                    { id: 'notifications', label: 'Varsler', Icon: Bell },
                    { id: 'account', label: 'Konto', Icon: Mail },
                    { id: 'subscription', label: 'Abonnement', Icon: Crown },
                    { id: 'help', label: 'Hjelp & Info', Icon: HelpCircle },
                    ...(!userProfile?.isAdmin ? [{ id: 'data', label: 'Mine Data', Icon: Download }] : []),
                  ].map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setSelectedSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedSection === section.id
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <section.Icon className="w-5 h-5" />
                      {section.label}
                    </button>
                  ))}
                </nav>
                <div className="px-3 py-4 border-t border-gray-200 mt-auto">
                  <button
                    onClick={signOutWithEmailGuard}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    Logg ut
                  </button>
                </div>
              </aside>

              {/* Content panel */}
              <div className="p-8 overflow-y-auto">
                {/* Messages */}
                {errorMessage && (
                  <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-red-700">{errorMessage}</p>
                  </div>
                )}

                {/* Privacy */}
                {selectedSection === 'privacy' && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Personvern</h2>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                          {hideOnlineStatus ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                          <div>
                            <p className="text-base text-gray-900">Skjul "Aktiv nå" status</p>
                            <p className="text-sm text-gray-500">Andre ser ikke om du er online</p>
                          </div>
                        </div>
                        <button onClick={() => handlePrivacyToggle(!hideOnlineStatus)} disabled={privacyLoading} className="flex items-center gap-2">
                          {privacyLoading && <Loader size="sm" className="text-purple-600" />}
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${hideOnlineStatus ? 'bg-purple-600' : 'bg-gray-300'} ${privacyLoading ? 'opacity-50' : ''}`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${hideOnlineStatus ? 'translate-x-6' : 'translate-x-1'}`} />
                          </div>
                        </button>
                      </div>
                      <div className="flex items-center justify-between py-4 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-gray-500" />
                          <div>
                            <p className="text-base text-gray-900">Motta markedsføring på e-post</p>
                            <p className="text-sm text-gray-500">Nyhetsbrev, tips og kampanjer</p>
                          </div>
                        </div>
                        <button onClick={() => handleMarketingConsentToggle(!marketingConsent)} disabled={marketingConsentLoading} className="flex items-center gap-2">
                          {marketingConsentLoading && <Loader size="sm" className="text-purple-600" />}
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${marketingConsent ? 'bg-purple-600' : 'bg-gray-300'} ${marketingConsentLoading ? 'opacity-50' : ''}`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${marketingConsent ? 'translate-x-6' : 'translate-x-1'}`} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notifications */}
                {selectedSection === 'notifications' && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Varsler</h2>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                          {pushSubscribed ? <Bell className="w-5 h-5 text-gray-500" /> : <BellOff className="w-5 h-5 text-gray-500" />}
                          <div>
                            <p className="text-base text-gray-900">Push-varsler</p>
                            <p className="text-sm text-gray-500">Motta melding og systemvarsler</p>
                          </div>
                        </div>
                        <button onClick={handlePushToggle} disabled={saving} className="flex items-center gap-2">
                          {saving && <Loader size="sm" className="text-purple-600" />}
                          <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${pushSubscribed ? 'bg-purple-600' : 'bg-gray-300'} ${saving ? 'opacity-50' : ''}`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${pushSubscribed ? 'translate-x-6' : 'translate-x-1'}`} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account */}
                {selectedSection === 'account' && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Konto</h2>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Mail className="w-4 h-4 text-gray-600" />
                        <p className="text-sm font-medium text-gray-700">Endre e-postadresse</p>
                      </div>
                      <form onSubmit={handleUpdateEmail} className="space-y-3 max-w-md">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => handleEmailInputChange(e.target.value)}
                          required
                          className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                          placeholder="ny@epost.no"
                        />
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm font-medium"
                        >
                          {loading ? 'Oppdaterer...' : 'Oppdater e-post'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {/* Subscription */}
                {selectedSection === 'subscription' && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Abonnement</h2>
                    {subscriptionData?.status === 'past_due' && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-sm text-red-800">
                          Din siste betaling feilet. Oppdater betalingsmetoden din for å beholde tilgangen.
                          {typeof subscriptionData?.daysLeftInGrace === 'number' && (
                            <span className="block mt-1 font-medium">
                              Du har {subscriptionData.daysLeftInGrace} dag{subscriptionData.daysLeftInGrace !== 1 ? 'er' : ''} igjen for å oppdatere betalingsdetaljene.
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                    {/* Active plan + next payment (same info as on Subscription page) */}
                    {(subscriptionStatusLoading || subscriptionData || userProfile?.subscription) && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                        {subscriptionStatusLoading ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader size="sm" />
                            <span className="text-sm">Laster abonnementsstatus...</span>
                          </div>
                        ) : (() => {
                          const plan = userProfile?.subscription || 'free';
                          const planLabels: Record<string, string> = {
                            free: 'Gratis',
                            plus: 'Talentfunn Pluss',
                            employer: 'Basis',
                            employer_plus: 'Pluss',
                          };
                          const planLabel = planLabels[plan] || plan;
                          const hasPaid = plan === 'plus' || plan === 'employer' || plan === 'employer_plus';
                          const endDate = subscriptionData?.currentPeriodEnd;
                          const formatEndDate = (dateString: string) => {
                            if (!dateString) return '';
                            return new Date(dateString).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });
                          };
                          const isCanceled = subscriptionData?.cancelAtPeriodEnd === true || subscriptionData?.cancel_at_period_end === true;
                          const daysRemaining = endDate
                            ? Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                            : null;
                          return (
                            <>
                              <p className="text-sm font-medium text-gray-700">
                                Aktiv plan: <span className="text-purple-600">{planLabel}</span>
                              </p>
                              {hasPaid && endDate && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {isCanceled && daysRemaining !== null
                                    ? `${daysRemaining} dager igjen til periodeslutt`
                                    : `Fornyes automatisk den ${formatEndDate(endDate)}`}
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                    {(() => {
                      const plan = userProfile?.subscription || 'free';
                      const hasPaid = plan === 'plus' || plan === 'employer' || plan === 'employer_plus';
                      return hasPaid ? (
                        <button
                          type="button"
                          onClick={() => onOpenUpdatePayment?.()}
                          disabled={!onOpenUpdatePayment}
                          className="flex items-center justify-between w-full py-4 px-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200 mb-3"
                        >
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-purple-600" />
                            <div className="text-left">
                              <p className="text-base text-gray-900">Endre betalingsmåte</p>
                              <p className="text-sm text-gray-500">Oppdater kort eller betalingsmetode</p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => navigateWithEmailGuard('subscription')}
                      className="flex items-center justify-between w-full py-4 px-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-purple-600" />
                        <div className="text-left">
                          <p className="text-base text-gray-900">Administrer abonnement</p>
                          <p className="text-sm text-gray-500">Se planer og fakturering</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                )}

                {/* Help & Info */}
                {selectedSection === 'help' && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Hjelp & Info</h2>
                    <div className="space-y-1">
                      <button onClick={() => navigateWithEmailGuard('faq')} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <HelpCircle className="w-5 h-5 text-gray-600" />
                          <span className="text-base text-gray-900">Ofte stilte spørsmål (FAQ)</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                      <button onClick={() => navigateWithEmailGuard('privacy')} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-gray-600" />
                          <span className="text-base text-gray-900">Personvernerklæring</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                      <button onClick={() => navigateWithEmailGuard('terms')} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <span className="text-base text-gray-900">Vilkår for bruk</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                      <button onClick={() => navigateWithEmailGuard('about')} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Info className="w-5 h-5 text-gray-600" />
                          <span className="text-base text-gray-900">Om Talentfunn</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    {/* Kontakt oss – Vipps compliance */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Kontakt oss</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p className="font-medium text-gray-900">Innovara AS</p>
                        <p>Org.nr. 933 184 048</p>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span>Blåknappveien 9, 4823 Nedenes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <a href="tel:94146523" className="text-purple-600 hover:underline">94 14 65 23</a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <a href="mailto:kontakt@talentfunn.no" className="text-purple-600 hover:underline">kontakt@talentfunn.no</a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Data / GDPR */}
                {selectedSection === 'data' && !userProfile?.isAdmin && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Mine Data</h2>
                    <div className="space-y-1">
                      <button onClick={handleExportData} disabled={exportLoading} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50">
                        <div className="flex items-center gap-3">
                          <Download className="w-5 h-5 text-blue-600" />
                          <div className="text-left">
                            <p className="text-base text-gray-900">Last ned mine data</p>
                            <p className="text-sm text-gray-500">Eksporter data i JSON-format (GDPR)</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                      <button onClick={() => setShowDeleteConfirm(true)} className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-red-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Trash2 className="w-5 h-5 text-red-600" />
                          <div className="text-left">
                            <p className="text-base text-red-600 font-medium">Slett kontoen min</p>
                            <p className="text-sm text-gray-500">Permanent sletting av all data</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </PageShell>

      {/* Push Notification Dialog */}
      {showDiscardEmailConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Forkaste e-postendring?</h3>
                <p className="text-sm text-gray-600">
                  Du har skrevet inn en ny e-postadresse som ikke er sendt ennå.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDiscardEmailConfirm(false);
                  setPendingEmailAction(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all active:scale-95 font-medium"
              >
                Fortsett redigering
              </button>
              <button
                onClick={confirmDiscardEmailChanges}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:shadow-lg transition-all active:scale-95 font-medium"
              >
                Forkast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Dialog */}
      {showPushDialog && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Aktiver push-varsler</h3>
              <button
                onClick={() => setShowPushDialog(false)}
                className="text-gray-400 hover:text-gray-600 active:scale-95 transition-transform"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Detect if mobile (has touch and small screen) */}
            {(() => {
              const isMobile = 'ontouchstart' in window && window.innerWidth < 768;
              return isMobile ? (
                <p className="text-sm text-gray-600 mb-5">
                  For å motta push-varsler om nye meldinger og aktivitet, må du aktivere varsler i nettleseren din.
                </p>
              ) : (
                <div className="text-sm text-gray-600 mb-5 space-y-3">
                  <p>
                    For å motta push-varsler, må du aktivere varsler manuelt i nettleserens innstillinger.
                  </p>
                  <p>
                    Klikk på ikon ved siden av URL-feltet (lås eller info-ikon) og aktiver varsler for Talentfunn.
                  </p>
                </div>
              );
            })()}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPushDialog(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all active:scale-95 font-medium"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirmPushDialog}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all active:scale-95 font-medium"
              >
                {'ontouchstart' in window && window.innerWidth < 768 ? 'Aktiver varsler' : 'Forstått'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Slett kontoen din?</h3>
                <p className="text-sm text-gray-600">
                  <strong>Advarsel:</strong> Dette vil permanent slette kontoen din og all tilknyttet data.
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
              <p className="text-sm text-gray-700 font-medium mb-2">
                Følgende data vil bli permanent slettet:
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Din profil og alle personopplysninger</li>
                <li>• Alle portfolioprosjekter og bilder</li>
                <li>• Alle meldinger og samtaler</li>
                <li>• Favoritter, notifikasjoner og innstillinger</li>
                <li>• Betalingsdata og abonnement</li>
              </ul>
              <p className="text-sm text-red-700 mt-3 font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                Du kan ikke angre denne handlingen.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50 active:scale-95 font-medium"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95 font-medium"
              >
                {deleteLoading ? (
                  <>
                    <Loader size="md" />
                    Sletter...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Ja, slett permanent
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
